import { Incoterms, PaymentEventEnum, PaymentMethodEnum } from 'api';
import {
  BankDetailsAutocomplete,
  BooleanSelectField,
  CheckboxField,
  PriceAndCurrencyField,
  SelectField,
  SwitchField,
  TextField,
  useFormContext,
  useWatch,
} from 'form';
import { useContext } from 'react';
import { Box, SectionContainer, SelectChangeEvent } from 'ui';

import { SalesOrderFormContext } from '~/orders/contexts/SalesOrderFormContext';
import { useVariablePricingEnabled } from '~/orders/hooks';
import { SalesOrderCreateFormValues } from '~/orders/pages/SalesOrderCreate/SalesOrderCreate';

import { VariablePricingFields } from '..';
import { InvoiceFinanceProviderSelectField } from '../InvoiceFinanceProvider/InvoiceFinanceProvider';
import { PaymentMethod } from '../PaymentMethod/PaymentMethod';

export const BuyerFinancialDetails = () => {
  const { setValue } = useFormContext<SalesOrderCreateFormValues>();
  const { variant, defaultValues } = useContext(SalesOrderFormContext);
  const { incoterm, purchaser_id, is_variable_pricing } = useWatch<SalesOrderCreateFormValues>();
  const isVariablePricingEnabled = useVariablePricingEnabled();

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    if (event.target.value === PaymentMethodEnum.DP) {
      setValue('due_date_offset', 0);
      setValue('due_date_direction', 1);
    }
  };

  return (
    <SectionContainer title="Financial Details">
      {isVariablePricingEnabled ? <SwitchField name="is_variable_pricing" label="Variable pricing" /> : null}
      <PriceAndCurrencyField
        data-testid="price-per-tonne"
        label="Price per tonne"
        priceName="price_per_tonne"
        currencyLabel="Seller Currency"
        currencyName="buyer_currency"
        required
        infoTooltip="Price per 1 tonne of net weight of the material."
      />
      <PriceAndCurrencyField
        data-testid="third-party-commission"
        label="Third Party comimission per tonne"
        priceName="third_party_commission_per_tonne"
        currencyLabel="Third Party Commission Currency"
        currencyName="third_party_commission_currency"
        infoTooltip="Commission paid to a third party or agent, calculated on per-tonne basis."
        required
      />
      <PaymentMethod
        sx={{ gridColumn: 'span 2' }}
        required={true}
        infoTooltip="Method using which purchaser will pay Safi."
        onChange={handlePaymentMethodChange}
      />
      <BooleanSelectField
        name="payment_must_be_received_before_safi_releases_the_documents"
        label="Payment before doc release"
        required
        infoTooltip="If Yes, Safi will not release the documents until the payment for the material is received in full."
      />
      <TextField
        type="number"
        label="Deposit Percentage upfront"
        name="deposit_percentage"
        infoTooltip="Percentage of the total amount that will be paid to Safi upfront, as a pre-payment."
      />
      <Box display="flex" flexDirection="row">
        <TextField type="number" label="Due date offset" name="due_date_offset" sx={{ flexShrink: 1 }} required />
        <SelectField
          label=""
          data-testid="due_date_direction"
          name="due_date_direction"
          fullWidth={true}
          infoTooltip="Number of days before or after the payment event the payment will be made to Safi by the purchaser."
          options={[
            {
              label: 'Before',
              value: '-1',
            },
            {
              label: 'After',
              value: '1',
            },
          ]}
        />
      </Box>
      <SelectField
        label="Payment Event"
        name="payment_event"
        options={getPaymentEventOptions(incoterm)}
        infoTooltip="Event that defines when the purchaser pays Safi for the material."
      />
      <CheckboxField name="is_trial_order" label="Is trial order" required />
      {variant === 'update' ? (
        <>
          <InvoiceFinanceProviderSelectField name="invoice_finance_provider" label="Invoice finance provider" />
          <BankDetailsAutocomplete
            name="bank_details_id"
            label="Bank details"
            tradeCompanies={purchaser_id ? [purchaser_id] : []}
            defaultValue={defaultValues?.bank_details_id}
          />
        </>
      ) : null}
      {isVariablePricingEnabled && is_variable_pricing ? (
        <VariablePricingFields defaultValues={defaultValues} formVariant={variant} />
      ) : null}
    </SectionContainer>
  );
};

const getPaymentEventOptions = (incoterm: Incoterms | undefined) => {
  switch (incoterm) {
    case 'EXW':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
      ];
    case 'FOB':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
      ];
    case 'CFR':
    case 'CIF':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
        {
          label: 'Destination port arrival',
          value: PaymentEventEnum.DA,
        },
        {
          label: 'ICD arrival',
          value: PaymentEventEnum.IA,
        },
        {
          label: 'Site Delivery',
          value: PaymentEventEnum.SD,
        },
      ];
    case 'CPT':
    case 'CIP':
      return [
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
      ];

    case 'DAP':
    case 'DDP':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
        {
          label: 'Destination port arrival',
          value: PaymentEventEnum.DA,
        },
        {
          label: 'ICD arrival',
          value: PaymentEventEnum.IA,
        },
        {
          label: 'Site Delivery',
          value: PaymentEventEnum.SD,
        },
      ];
    default:
      return [];
  }
};
